/**
 * Résolution des retours contextuels (`?from=`) des pages de détail.
 *
 * Les query params arrivent bruts du client : on ne les réfléchit JAMAIS tels
 * quels dans un href (anti open-redirect). Chaque contexte est résolu via une
 * whitelist, et les identifiants (équipe, email) sont validés puis ré-encodés.
 */

// ─── Activation L16 (MASTER-GEST-PROFILE) ────────────────────────────────────
// Ce module etait porte mais SANS AUCUN consommateur : /profile est son premier
// (F-813). Deux adaptations au parc, faites ICI plutot que dupliquees au callsite :
//
//  1. `labelKey` (cle i18n) au lieu du `label` FR en dur du PoC, sur
//     resolveProfileBack — le parc est bilingue natif (RÈGLES I18N).
//  2. WHITELIST REDUITE AUX ROUTES QUI EXISTENT : re-ouverte progressive —
//     `mon-equipe` (L10), `grille` (GC-D), `admin` (AD-C), `admin-remuneration`
//     (AD-D). Les origines non portees retombent sur `default: null`.
//
// resolvePlanningBack : premier consommateur = /planification-salariale (SL-A,
// F-605). Meme adaptation au parc que resolveProfileBack en L16 : `labelKey`
// (cle i18n) au lieu du `label` FR en dur du PoC — le parc est bilingue natif.

/** Cible de retour de la planification (F-605) — `labelKey` = cle i18n, jamais un libelle. */
export type PlanningBackTarget = { href: string; labelKey: string }

/** Cible de retour du profil (F-813) — `labelKey` = cle i18n, jamais un libelle. */
export type ProfileBackTarget = { href: string; labelKey: string }

const TEAM_ID_RE = /^[A-Za-z0-9_-]+$/
const EMAIL_RE = /^[^@\s]+@[^@\s]+$/

function safeTeamId(raw: string | undefined | null): string | null {
  return raw && TEAM_ID_RE.test(raw) ? raw : null
}

function safeEmail(raw: string | undefined): string | null {
  const email = raw?.toLowerCase().trim()
  return email && EMAIL_RE.test(email) ? email : null
}

/** F-813 : retour depuis le profil membre (`/profile/:email?from=…&fromTeam=…`). */
export function resolveProfileBack(
  from?: string | null,
  fromTeam?: string | null
): ProfileBackTarget | null {
  const teamId = safeTeamId(fromTeam)
  switch (from) {
    case 'organigramme':
      return { href: '/organigramme', labelKey: 'gest:profile.back.organigramme' }
    case 'equipes':
      return teamId
        // teamId valide par TEAM_ID_RE PUIS re-encode : aucune valeur brute du
        // client n'atteint l'href.
        ? { href: `/equipes/${encodeURIComponent(teamId)}`, labelKey: 'gest:profile.back.team' }
        : { href: '/equipes', labelKey: 'gest:profile.back.teams' }
    // RE-OUVERT par L10 (MASTER-GEST-MON-EQUIPE) : /mon-equipe existe desormais,
    // et ses cartes membres pointent /profile/{email}?tab=gestion&from=mon-equipe
    // &fromTeam= (F-162). La whitelist l'avait retire tant que l'ecran manquait —
    // c'est exactement le « a re-ouvrir au fur et a mesure » de l'en-tete.
    // `?team=` n'est repropage que si fromTeam est valide (meme garde que 'equipes').
    case 'mon-equipe':
      return teamId
        ? { href: `/mon-equipe?team=${encodeURIComponent(teamId)}`, labelKey: 'gest:profile.back.monEquipe' }
        : { href: '/mon-equipe', labelKey: 'gest:profile.back.monEquipe' }
    // RE-OUVERT par L06 GC-D (grille salariale CE, PDR-EQU-04) : /grille-salariale
    // existe desormais, et les cartes de l'onglet Cadres pointent
    // /profile/{email}?tab=gestion&from=grille. Le retour renvoie au tab Cadres
    // (l'ecran d'ou vient la navigation). Meme motif « a re-ouvrir au fur et a mesure ».
    case 'grille':
      return { href: '/grille-salariale?tab=cadres', labelKey: 'gest:profile.back.grille' }
    // RE-OUVERT par AD-C (PDR-EQU-04 resorbe) : l'ecran /admin existe desormais,
    // et les liens « voir profil » de l'onglet Membres pointent
    // /profile/{email}?from=admin. Le retour renvoie au bon onglet (members).
    case 'admin':
      return { href: '/admin?tab=members', labelKey: 'gest:profile.back.admin' }
    // RE-OUVERT par AD-D (PDR-EQU-04 admin-remuneration) : l'onglet Remuneration
    // pointe /profile/{email}?tab=gestion&from=admin-remuneration. Le retour
    // renvoie au bon onglet (remuneration), pas a members.
    case 'admin-remuneration':
      return { href: '/admin?tab=remuneration', labelKey: 'gest:profile.back.adminRemuneration' }
    default:
      // Origines PoC non portees : mieux vaut aucun BackLink qu'un lien vers un 404.
      return null
  }
}

/** Retour depuis la planification salariale (`/planification-salariale?from=…`). */
export function resolvePlanningBack({
  from,
  member,
  team,
}: {
  from?: string
  member?: string
  team?: string
}): PlanningBackTarget | null {
  switch (from) {
    case 'mon-equipe': {
      const teamId = safeTeamId(team)
      return {
        href: teamId ? `/mon-equipe?team=${teamId}` : '/mon-equipe',
        labelKey: 'gest:planification.back.monEquipe',
      }
    }
    case 'profile': {
      const email = safeEmail(member)
      return email
        ? {
            href: `/profile/${encodeURIComponent(email)}?tab=gestion`,
            labelKey: 'gest:planification.back.profile',
          }
        : null
    }
    default:
      return null
  }
}

/**
 * Filtre les params de contexte à re-propager dans les hrefs (onglets du
 * profil) : seules les valeurs qui résolvent vers un retour valide passent.
 */
export function profileBackQuery(
  from?: string | null,
  fromTeam?: string | null
): Record<string, string> | undefined {
  if (!resolveProfileBack(from, fromTeam)) return undefined
  return {
    from: from as string,
    ...(safeTeamId(fromTeam) ? { fromTeam: fromTeam as string } : {}),
  }
}
